import ProtectedRoute from "components/ProtectedRoute";
import { notify } from "components/ui";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTokenRefresh } from "hooks/useTokenRefresh";
import { useTranslation } from "hooks/useTranslation";
import { authService } from "services/authService";
import { useEffect, useRef, useState } from "react";

const IMPERSONATION_PARAM = "impersonationToken";

// The super-admin app puts the token in the fragment, which never reaches a server or a Referer header;
// the query form is still read so a tab opened by an older build keeps working.
const readImpersonationToken = (search: string, hash: string): string | null =>
  new URLSearchParams(hash.replace(/^#/, "")).get(IMPERSONATION_PARAM) ??
  new URLSearchParams(search).get(IMPERSONATION_PARAM);

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasCheckedToken = useRef(false);
  const [impersonationToken] = useState(() =>
    readImpersonationToken(location.search, location.hash),
  );
  const [checkingImpersonation, setCheckingImpersonation] = useState(
    impersonationToken !== null,
  );

  // A link must not be able to swap the signed-in session for someone else's (SEC-26): the token is
  // taken out of the address bar at once, and only replaces the session once the API accepts it as an
  // impersonation token.
  useEffect(() => {
    if (!impersonationToken || hasCheckedToken.current) return;
    hasCheckedToken.current = true;
    navigate(location.pathname, { replace: true });

    authService
      .startImpersonation(impersonationToken)
      .then((started) => {
        if (started) {
          navigate("/admin/dashboard", { replace: true });
        } else {
          notify({ type: "error", title: t("app.auth.impersonationInvalid") });
        }
      })
      .finally(() => setCheckingImpersonation(false));
  }, [impersonationToken, location.pathname, navigate, t]);

  // Automatically refresh tokens before they expire
  useTokenRefresh();

  if (checkingImpersonation) return null;

  return (
    <>
      <Routes>
        <Route path="auth/*" element={<AuthLayout />} />
        <Route
          path="admin/*"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
        {/* Catch all unknown routes and redirect to dashboard */}
        <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
    </>
  );
};

export default App;
